import React, { Suspense, useState, useEffect, useMemo, useRef } from 'react';
import { Avatar, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import { useGetBenchmarkingQuery, useGetBenchmarkOverwriteQuery, useGetPortfolioAnalysisHeatQuery, useGetPortfolioAnalysisPowerQuery, useGetPortfolioAnalysisSubscriptionQuery, useGetPortfolioAnalysisWaterQuery } from '../../../api/energy-analysis/portfolioAnalysisApiSlice';
import MeterContainer from './MeterContainer';
import { useTranslation } from 'react-i18next';
import BoltIcon from '@mui/icons-material/Bolt';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';

const PortfolioAnalysisDashboardDatagrid = React.lazy(() => import('remote/PortfolioAnalysisDashboardDatagrid').catch(() => ({ default: () => <div>Failed to load</div> })));
const PortfolioAnalysisServiceCard = React.lazy(() => import('remote/PortfolioAnalysisServiceCard').catch(() => ({ default: () => <div>Failed to load</div> })));

const formatNumber = (number, decimals) => {
  return new Intl.NumberFormat('da-DK', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(number);
}

const ensureAllTypes = (item, list) => {
  const types = ['power', 'heat', 'water']
  
  let group = list.filter(i => i.matrikel_key === item.matrikel_key)
  
  return types.every(type => group.some(g => g.type === type));

}

const PortfolioAnalysisDashboardPage = ({

}) => {

  const { t } = useTranslation('portfolio-analysis_dashboard')
  const dispatch = useDispatch()

  const [isOpen, setIsOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState({})
  
  const handleSetSelectedMeter = (meter) => {
    setIsOpen(true);
    setSelectedMeter(meter);
  }
  
  const [meterType, setMeterType] = useState('')
  const [outOfSync, setOutOfSync] = useState(false);
  const account = useSelector(getSelectedAccount)

  const { data: subscriptionData, isLoading: subscriptionLoading, isError: subscriptionError, isSuccess: subscriptionSuccess, isFetching: isSubscriptionFetching } = useGetPortfolioAnalysisSubscriptionQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId });

  const warned = useRef(false);

  useEffect(() => {
    if (subscriptionError && !warned.current) {
      warned.current = true;
      dispatch(showSnackbar({ message: t('noSubscriptions'), severity: 'warning' }));
    }
  }, [subscriptionError]);

  const { data: powerData, error: powerError, isLoading: isPowerLoading, isFetching: isPowerFetching } = useGetPortfolioAnalysisPowerQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId || !subscriptionData?.power });
  const { data: waterData, error: waterError, isLoading: isWaterLoading, isFetching: isWaterFetching } = useGetPortfolioAnalysisWaterQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId || !subscriptionData?.water });
  const { data: heatData, error: heatError, isLoading: isHeatLoading, isFetching: isHeatFetching } = useGetPortfolioAnalysisHeatQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId || !subscriptionData?.heat });

  const isDatagridFetching = useMemo(() => isPowerFetching || isWaterFetching || isHeatFetching || isSubscriptionFetching, [isPowerFetching, isWaterFetching, isHeatFetching, isSubscriptionFetching])
    const { data: benchmarkData, isError: benchmarkError, isLoading: benchmarkLoading, isFetching: benchmarkFetching } = useGetBenchmarkingQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId })
    const { data: benchmarkOverwriteData, isError: isBenchmarkOverwriteError, isLoading: isBenchmarkOverwriteLoading } = useGetBenchmarkOverwriteQuery({ account_id: account?.AccountId }, { skip: !account?.AccountId });


  const rows = useMemo(() => {
    let tempHeat = heatData?.portfolio_analysis_list ? [...heatData.portfolio_analysis_list?.map(o => ({ heat_total_consumption_year: o.total_consumption_year, type: 'heat', ...o, metering_point_id: JSON.parse(o.data_stream).unit_id }))] : []
    let tempPower = powerData?.portfolio_analysis_list ? [...powerData.portfolio_analysis_list?.map(o => ({ power_total_consumption_year: o.total_consumption_year, type: 'power', ...o }))] : []
    let tempWater = waterData?.portfolio_analysis_list ? [...waterData.portfolio_analysis_list?.map(o => ({ water_total_consumption_year: o.total_consumption_year, type: 'water', ...o, metering_point_id: JSON.parse(o.data_stream).unit_id }))] : []
    let list =  [...tempHeat, ...tempPower, ...tempWater].filter(item => item.matrikel_key)
    list = list.filter(item => ensureAllTypes(item, list))
    return {portfolio_analysis_list: list}
  }, [powerData, waterData, heatData])

  const enrichedData = useMemo(() => {
    if (rows != null && benchmarkData == null) return rows
    if (rows == null || benchmarkData == null) return null
    let temp = {
      ...rows, portfolio_analysis_list: rows.portfolio_analysis_list.map(o => {
        let benchmarkEntry = benchmarkData.BenchmarkData.find(b => b.MatrikelKey == o?.matrikel_key)
        let benchmarkOverwriteEntry = benchmarkOverwriteData?.find(b => b.MatrikelKey == o?.matrikel_key) || {}
        return { ...o, ...benchmarkEntry, overwrite: benchmarkOverwriteEntry }
      })
    }
    return temp
  }, [rows, benchmarkData])


  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, maxHeight: "90vh" }}>
      <Grid container spacing={2} maxHeight={'90vh'}>
        <Suspense fallback={<Skeleton />}>
          {subscriptionData && [
            {
              disabled: !subscriptionData?.power,
              label: t('cards.power.title'),
              path: '/portfolio-analysis-hub/power',
              value: enrichedData.portfolio_analysis_list.reduce((acc, o) => o.power_total_consumption_year ? acc + o.power_total_consumption_year : acc, 0),
              unit: t('cards.power.unit'),
              icon: <BoltIcon/>,
              color: '#FFC107',
              subheader: (
                <Box display={'flex'} flexDirection={'column'} mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.area')}: {enrichedData.portfolio_analysis_list.reduce((acc, o) => o.SumBuildingArea && o.type === 'power' ? acc + o.SumBuildingArea : acc, 0)} {t('cards.buildings.unit')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.count')}: {new Set(enrichedData.portfolio_analysis_list.filter(item => item.type === 'power').map(item => item.main_address)).size}
                  </Typography>
                </Box>
              ),
              summedData: powerData?.summed_data ?? []
            },
            {
              disabled: !subscriptionData?.heat,
              label: t('cards.heat.title'),
              path: '/portfolio-analysis-hub/heat',
              value: enrichedData.portfolio_analysis_list.reduce((acc, o) => o.heat_total_consumption_year ? acc + o.heat_total_consumption_year : acc, 0),
              unit: t('cards.heat.unit'),
              icon: <LocalFireDepartmentRoundedIcon/>,
              color: '#FFA726',
              subheader: (
                <Box display={'flex'} flexDirection={'column'} mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.area')}: {enrichedData.portfolio_analysis_list.reduce((acc, o) => o.SumBuildingArea && o.type === 'heat' ? acc + o.SumBuildingArea : acc, 0)} {t('cards.buildings.unit')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.count')}: {new Set(enrichedData.portfolio_analysis_list.filter(item => item.type === 'heat').map(item => item.main_address)).size}
                  </Typography>
                </Box>
              ),
              summedData: heatData?.summed_data ?? []
            },
            {
              disabled: !subscriptionData?.water,
              label: t('cards.water.title'),
              path: '/portfolio-analysis-hub/water',
              value: enrichedData.portfolio_analysis_list.reduce((acc, o) => o.water_total_consumption_year ? acc + o.water_total_consumption_year : acc, 0),
              unit: t('cards.water.unit'),
              icon: <WaterDropRoundedIcon/>,
              color: '#1E88E5',
              subheader: (
                <Box display={'flex'} flexDirection={'column'} mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.area')}: {enrichedData.portfolio_analysis_list.reduce((acc, o) => o.SumBuildingArea && o.type === 'water' ? acc + o.SumBuildingArea : acc, 0)} {t('cards.buildings.unit')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('cards.buildings.count')}: {new Set(enrichedData.portfolio_analysis_list.filter(item => item.type === 'water').map(item => item.main_address)).size}
                  </Typography>
                </Box>
              ),
              summedData: waterData?.summed_data ?? []

            },
          ].map(service => (
            <Grid item lg={4} key={service.label}>
              <PortfolioAnalysisServiceCard {...service} />
            </Grid>
          ))}
        </Suspense>
        <Grid item lg={12} maxHeight={'80vh'}>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', height: '80vh', verticalAlign: 'center' }}>
                <CircularProgress />
              </Box>
            }
          >
            <Box minHeight={'50vh'} maxHeight={'75vh'} display={'flex'} flexDirection={'column'}>
              <PortfolioAnalysisDashboardDatagrid
                data={enrichedData}
                setSelectedMeter={handleSetSelectedMeter}
                setMeterType={setMeterType}
                outOfSync={false}
                isLoading={isDatagridFetching}
              />
            </Box>
          </Suspense>
        </Grid>
      </Grid>
      <MeterContainer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        meter={selectedMeter}
        meterType={meterType}
        analysisType={selectedMeter?.type}
      />
    </Box>
  );
}


export default PortfolioAnalysisDashboardPage


